import React, { CSSProperties, useMemo } from 'react';
import clsx from 'clsx';
import { themeColours, themeType, type ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import { storyLayout, type StoryItem } from 'fym-shared/src/music24/story/storyLayout';
import {
  STORY_BLUR,
  STORY_BLUR_SCRIM_OPACITY,
  STORY_GROUND_ON_BLUR_OPACITY,
  storyCoverFrame,
  storyDeco,
  storyGroundBackground,
  storyOrnamentColour,
  storyRecord,
  type StoryColours,
} from 'fym-shared/src/music24/story/storyStyles';
import type { KitStory, StoryStyle } from 'fym-shared/src/music24/story/storyTypes';
import { watermarkTone } from 'fym-shared/src/music24/story/watermark';
import { TypedMemo } from '../../../../common/types';
import { kitFontName, useKitWebFonts } from '../../themes/kitWebFonts';
import { StoryPreviewText, type StoryFontCss } from './StoryPreviewText';
import styles from './StoryPreview.module.scss';
import { WatermarkLockup } from './WatermarkLockup';

interface StoryPreviewProps {
  story: KitStory;
  look: ReleaseKitLook;
  palette: readonly string[];
  /** The Story style: card, ground or type. */
  storyStyle: StoryStyle;
  artworkUrl: string | undefined;
  /** The kit logo; null with no logo. */
  logoUrl: string | null;
  /** Free plans: the watermark of the file. */
  watermark?: boolean;
  /** The width of the preview in px; the height follows the frame of the format (9:16, 4:5 or 1:1). */
  width: number;
  className?: string;
}

const FULL: CSSProperties = { position: 'absolute', inset: 0 };

function fontCss(fontId: Parameters<typeof kitFontName>[0], style: ReturnType<typeof themeType>['text']): StoryFontCss {
  return {
    fontFamily: kitFontName(fontId),
    fontWeight: style.weight,
    fontStyle: style.italic ? 'italic' : 'normal',
    textTransform: style.case === 'none' ? 'none' : style.case,
    letterSpacing: `${style.tracking}em`,
    tracking: style.tracking,
  };
}

const Cover: React.FC<{
  item: Extract<StoryItem, { kind: 'cover' }>;
  themeId: string;
  colors: StoryColours;
  artworkUrl: string | undefined;
  unit: number;
}> = ({ item, themeId, colors, artworkUrl, unit }) => {
  const { outer, picture, tape } = storyCoverFrame(themeId, item.small, colors, unit);
  return (
    <div
      style={{
        position: 'absolute',
        left: item.box.x,
        top: item.box.y,
        width: item.box.w,
        boxSizing: 'border-box',
        ...outer,
      }}
    >
      {item.withRecord && <div style={storyRecord(colors, unit)} />}
      {artworkUrl === undefined ? (
        <div className={styles.picture} style={{ background: colors.accent, ...picture }} />
      ) : (
        <img className={styles.picture} src={artworkUrl} alt="" style={picture} />
      )}
      {tape !== null && <div style={tape} />}
    </div>
  );
};

/**
 * The Story and feed post preview of the Studio (design `studio-details/a Image details`, frames a1
 * to a4; the feed post is the Story stack in its 4:5 or square frame, design `asset-redesign/e`). It is
 * drawn in the browser with the code of the render: the layout (`storyLayout`) and the look of each
 * part (`storyStyles`) come from fym-shared, the colours from `themeColours` of the cover, the type
 * from `themeType`. Only the crop of the cover can differ: the director picks it.
 */
const StoryPreviewComponent: React.FC<StoryPreviewProps> = ({
  story,
  look,
  palette,
  storyStyle,
  artworkUrl,
  logoUrl,
  watermark = false,
  width,
  className,
}) => {
  useKitWebFonts();
  const { themeId } = look;
  const type = useMemo(() => themeType(look), [look]);
  const colors = useMemo<StoryColours>(() => {
    const colours = themeColours(themeId, palette);
    return {
      ink: colours.background,
      surface: colours.surface,
      text: colours.text,
      accent: colours.accent,
      onAccent: colours.onAccent,
    };
  }, [palette, themeId]);
  const withLogo = story.withLogo && logoUrl !== null;
  const layout = useMemo(
    () =>
      storyLayout({
        story: { ...story, withLogo },
        themeId,
        style: storyStyle,
        fonts: type.fonts,
        typeStyle: { display: type.display, text: type.text },
        watermark,
      }),
    [story, withLogo, themeId, storyStyle, type, watermark]
  );
  const display = useMemo(() => fontCss(type.fonts.display, type.display), [type]);
  const text = useMemo(() => fontCss(type.fonts.text, type.text), [type]);
  const ground = storyGroundBackground(themeId, colors);
  const onBlur = storyStyle === 'ground' && artworkUrl !== undefined;
  const { format } = layout;
  const { unit } = format;
  const scale = width / format.size.width;

  return (
    <div
      className={clsx(styles.frame, className)}
      style={{ width, height: format.size.height * scale }}
      data-testid="story-preview"
      data-format={format.id}
    >
      <div
        className={styles.canvas}
        style={{
          width: format.size.width,
          height: format.size.height,
          transform: `scale(${scale})`,
          background: colors.ink,
          color: colors.text,
          fontFamily: text.fontFamily,
        }}
      >
        {onBlur && (
          <>
            <img src={artworkUrl} alt="" style={STORY_BLUR} />
            <div style={{ ...FULL, background: colors.ink, opacity: STORY_BLUR_SCRIM_OPACITY }} />
          </>
        )}
        {ground !== null && (
          <div style={{ ...FULL, background: ground, opacity: onBlur ? STORY_GROUND_ON_BLUR_OPACITY : 1 }} />
        )}
        {storyDeco(themeId, colors, format.id).map((deco) => (
          <div key={JSON.stringify(deco)} style={deco} />
        ))}
        {layout.items.map((item) => {
          const key = `${item.kind}-${item.box.x}-${item.box.y}`;
          if (item.kind === 'logo') {
            return logoUrl === null ? null : (
              <img
                key={key}
                src={logoUrl}
                alt=""
                style={{
                  position: 'absolute',
                  left: item.box.x,
                  top: item.box.y,
                  width: item.box.w,
                  height: item.box.h,
                  objectFit: 'contain',
                }}
              />
            );
          }
          if (item.kind === 'cover') {
            return (
              <Cover key={key} item={item} themeId={themeId} colors={colors} artworkUrl={artworkUrl} unit={unit} />
            );
          }
          if (item.kind === 'watermark') {
            // Owner answer 2026-10-08 and design w0: the lockup, the last item of the stack under the SmartLink line.
            return (
              <WatermarkLockup
                key={key}
                testId="story-preview-watermark"
                lockup={item.lockup}
                tone={watermarkTone(colors.ink, false)}
                x={item.box.x}
                y={item.box.y}
              />
            );
          }
          if (item.kind === 'ornament') {
            return (
              <div
                key={key}
                style={{
                  position: 'absolute',
                  left: item.box.x,
                  top: item.box.y,
                  width: item.box.w,
                  height: item.box.h,
                  background: storyOrnamentColour(layout.frame, colors),
                }}
              />
            );
          }
          return (
            <StoryPreviewText
              key={key}
              item={item}
              story={story}
              frame={layout.frame}
              themeId={themeId}
              display={display}
              text={text}
              colors={colors}
              unit={unit}
            />
          );
        })}
      </div>
    </div>
  );
};

export const StoryPreview = TypedMemo(StoryPreviewComponent);
